<template>
  <div>
    <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-y-scroll">
      <div class="sm:mx-auto sm:w-full sm:max-w-full">
        <div class="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div class="py-8 px-4 sm:px-10">
            <div class="md:grid md:grid-cols-12 md:divide-x md:divide-gray-200">
              <section class="md:col-span-12 mb-12 lg:mb-0 lg:col-span-4 mt-12 md:mt-0 md:pr-14">
                <img class="mb-4 h-16 w-auto m-auto" :src="team.companyLogo" />
                <template v-if="!reschedulingEvent">
                  <h2 class="text-base font-semibold leading-6 text-gray-900">
                    {{ team.name }} - {{ team.eventName }}
                  </h2>
                  <p class="mt-4 space-y-1 text-sm leading-6 text-gray-500">{{ team.eventDescription }}</p>
                </template>
                <template v-else>
                  <h2 class="text-base font-semibold leading-6 text-gray-900">
                    Reschedule {{ existingEvent.eventName }}
                  </h2>
                  <p class="mt-4 space-y-1 text-sm leading-6 text-gray-500">
                    Existing event - {{ formatDate(existingEvent.startTime, 'MMMM dd, yyyy') }}
                    {{ formatDate(existingEvent.startTime, 'h:mm a') }} -
                    {{ formatDate(existingEvent.endTime, 'h:mm a') }}
                  </p>
                </template>
              </section>
              <div class="mt-12 md:mt-0 md:col-span-6 lg:col-span-4 md:mt-0 md:pl-14 md:pr-14">
                <div class="flex items-center">
                  <h2 class="flex-auto text-base font-semibold text-gray-900">
                    {{ formatDate(currentMonth, 'MMMM yyyy') }}
                  </h2>
                  <button
                    @click="previousMonth()"
                    type="button"
                    class="-my-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                  >
                    <span class="sr-only">Previous month</span>
                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path
                        fill-rule="evenodd"
                        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    @click="nextMonth()"
                    type="button"
                    class="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                  >
                    <span class="sr-only">Next month</span>
                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path
                        fill-rule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                <div class="mt-10 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
                  <div>S</div>
                  <div>M</div>
                  <div>T</div>
                  <div>W</div>
                  <div>T</div>
                  <div>F</div>
                  <div>S</div>
                </div>
                <div class="mt-2 grid grid-cols-7 text-sm">
                  <div v-for="day of calendarDays" :key="day.toString()" class="py-2">
                    <button
                      @click="changeDay(day)"
                      type="button"
                      :class="
                        isDateSame(day, currentDay, 'day')
                          ? 'mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-white'
                          : hasAvailableSlots(day)
                            ? 'mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-gray-200'
                            : 'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-gray-900 hover:bg-gray-200'
                      "
                    >
                      {{ formatDate(day, 'd') }}
                    </button>
                  </div>
                </div>
              </div>
              <section v-if="!showForm" class="md:col-span-6 lg:col-span-4 mt-12 md:mt-0 md:pl-14">
                <h2 class="text-base font-semibold leading-6 text-gray-900">
                  Availability for {{ formatDate(currentDay, 'MMMM dd, yyyy') }}
                </h2>
                <ol class="max-h-80 overflow-auto mt-8 space-y-1 text-sm leading-6 text-gray-500">
                  <li
                    v-for="event of currentDaySlots"
                    :key="event.id"
                    @click="
                      selectedSlot = event.id;
                      selectedSlotStartTime = event.startTime;
                      selectedSlotEndTime = event.endTime;
                    "
                    :class="
                      selectedSlot == event.id
                        ? 'cursor-pointer group flex items-center space-x-4 rounded-xl px-4 py-2 bg-gray-900 text-white'
                        : 'cursor-pointer group flex items-center space-x-4 rounded-xl px-4 py-2 focus-within:bg-gray-100 hover:bg-gray-100'
                    "
                  >
                    <div class="flex-auto">
                      <p class="mt-0.5">
                        {{ formatDate(event.startTime, 'h:mm a') }} - {{ formatDate(event.endTime, 'h:mm a') }}
                      </p>
                    </div>
                  </li>

                  <!-- More meetings... -->
                </ol>
                <LoadingSpinner />
                <p
                  v-if="!loading && currentDaySlots.length == 0"
                  class="mt-4 space-y-1 text-sm leading-6 text-gray-500"
                >
                  No availability on this day
                </p>
                <div :class="selectedSlot != '' ? 'mt-4 flex justify-end' : 'mt-4 flex justify-end invisible'">
                  <TButton class="mr-2" label="Cancel" color="white" @click="selectedSlot = ''"> </TButton>
                  <TButton label="Reserve Slot" color="indigo" @click="showForm = true"> </TButton>
                </div>
              </section>
              <section v-else class="md:col-span-6 lg:col-span-4 mt-12 md:mt-0 md:pl-14">
                <h2 class="text-base font-semibold leading-6 text-gray-900 mb-6">{{ selectedSlot }}</h2>
                <TextInput
                  label="First Name"
                  placeholder="Elon"
                  :modelValue="candidate.firstName"
                  @update:modelValue="candidate.firstName = $event"
                  :disabled="$route.query.event_type == 'humaniz_checkin' ? false : true"
                />
                <TextInput
                  class="mt-4"
                  label="Last Name"
                  placeholder="Husk"
                  v-model="candidate.lastName"
                  :disabled="$route.query.event_type == 'humaniz_checkin' ? false : true"
                />
                <PhoneInput
                  class="mt-4"
                  label="Phone Number"
                  placeholder="+1 (281) 330 8004"
                  :modelValue="candidate.phone"
                  @update:modelValue="candidate.phone = $event"
                  :disabled="$route.query.event_type == 'humaniz_checkin' ? false : true"
                />
                <TextInput
                  class="mt-4"
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  v-model="candidate.email"
                  :disabled="$route.query.event_type == 'humaniz_checkin' ? false : true"
                />
                <div class="mt-4 flex justify-end">
                  <TButton class="mr-2" label="Cancel" color="white" @click="showForm = false"> </TButton>
                  <TButton
                    :label="reschedulingEvent ? 'Confirm Reschedule' : 'Confirm'"
                    color="indigo"
                    @click="createEvent"
                  >
                    <LocalLoadingSpinner />
                  </TButton>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Alert ref="alert" />
  </div>
</template>

<script>
import Alert from '../shared/Alert.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import DateUtility from '../../utilities/dates';
import { addMonths, startOfMonth, startOfWeek, endOfMonth, endOfWeek } from 'date-fns';

export default {
  components: {
    Alert,
    LoadingSpinner,
  },
  data() {
    return {
      team: {
        companyLogo: '',
        name: '',
        eventName: '',
        eventDescription: '',
      },
      candidate: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      existingEvent: {
        eventName: '',
        startTime: '',
        endTime: '',
      },
      reschedulingEvent: false,
      calendarDays: [],
      currentMonth: new Date(),
      currentDay: new Date(),
      eventSlots: [],
      selectedSlot: '',
      selectedSlotStartTime: '',
      selectedSlotEndTime: '',
      showForm: false,
      loading: false,
    };
  },
  mounted() {
    this.getTeam();
    this.getEventSlots();
    this.getDays();
  },
  methods: {
    ...DateUtility,
    getTeam() {
      const self = this;
      let teamUrl = self.$route.query.contact_id
        ? `/public/contact_interview/team?id=${self.$route.params.id}&contact_id=${self.$route.query.contact_id}&event_type=${self.$route.query.event_type}`
        : `/public/contact_interview/team?id=${self.$route.params.id}&event_type=${self.$route.query.event_type}&humaniz_checkin_user_id=${self.$route.query.humaniz_checkin_user_id}`;
      fetch(teamUrl)
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.team.name = data.company;
          self.team.eventName = data.event_name;
          self.team.eventDescription = data.event_description;
          self.team.companyLogo = data.company_logo;
          if (data.contact) {
            self.candidate.firstName = data.contact.first_name;
            self.candidate.lastName = data.contact.last_name;
            self.candidate.email = data.contact.email;
            self.candidate.phone = data.contact.phone_number;
          }
          if (data.existing_event) {
            self.existingEvent.eventName = data.existing_event.name;
            self.existingEvent.startTime = data.existing_event.start_time;
            self.existingEvent.endTime = data.existing_event.end_time;
            self.reschedulingEvent = true;
          }
        });
    },
    getEventSlots() {
      const self = this;
      self.loading = true;
      self.$store.commit('general/showLoadingSpinner');
      fetch(
        `/public/contact_interview/${self.$route.params.id}?event_type=${self.$route.query.event_type}&contact_id=${self.$route.query.contact_id}&humaniz_checkin_user_id=${self.$route.query.humaniz_checkin_user_id}`
      )
        .then(response => {
          return response.json();
        })
        .then(data => {
          if (data.status != 'error') {
            let foundSlots = [];
            for (let slot of data.slots) {
              foundSlots.push({
                startTime: slot.start_time,
                endTime: slot.end_time,
                id: `${self.formatDate(slot.start_time, 'MMMM dd, h:mm a')} - ${self.formatDate(
                  slot.end_time,
                  'h:mm a'
                )}`,
              });
            }
            self.eventSlots = foundSlots;
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Event Scheduling Error',
              alertDescription:
                'Calendar unavailable at the moment. Please try again later or reach out to schedule a time via text message.',
            });
          }
          self.$store.commit('general/hideLoadingSpinner');
          self.loading = false;
        });
    },
    async createEvent() {
      this.$store.commit('general/showLocalLoadingSpinner');

      // Build URL parameters dynamically
      const params = new URLSearchParams();
      if (this.$route.query.event_type) params.append('event_type', this.$route.query.event_type);
      if (this.$route.query.contact_id) params.append('contact_id', this.$route.query.contact_id);
      if (this.$route.query.humaniz_checkin_user_id)
        params.append('humaniz_checkin_user_id', this.$route.query.humaniz_checkin_user_id);

      try {
        const response = await fetch(`/public/contact_interview?${params.toString()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
          body: JSON.stringify({
            id: this.$route.params.id,
            start_time: this.selectedSlotStartTime,
            end_time: this.selectedSlotEndTime,
            first_name: this.candidate.firstName,
            last_name: this.candidate.lastName,
            email: this.candidate.email,
            phone_number: this.candidate.phone,
          }),
        });

        const data = await response.json();
        this.$store.commit('general/hideLocalLoadingSpinner');

        if (data.status != 'error') {
          this.$store.commit('general/showAlert', {
            alertType: 'success',
            alertTitle: 'Event succesfully scheduled!',
            alertDescription: 'Your event has been successfully scheduled. Use the button below to dismiss this alert.',
          });
          this.showForm = false;
          this.selectedSlot = '';
          this.selectedSlotStartTime = '';
          this.selectedSlotEndTime = '';
          this.getTeam();
          this.getEventSlots();
          this.getDays();
        } else {
          this.$store.commit('general/showAlert', {
            alertType: 'error',
            alertTitle: 'Event schedule error',
            alertDescription: data.message,
          });
        }
      } catch (error) {
        this.$store.commit('general/hideLocalLoadingSpinner');
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Network Error',
          alertDescription: 'Failed to schedule event. Please try again.',
        });
      }
    },
    getDays() {
      let firstDayOfMonth = startOfMonth(this.currentMonth);
      let firstDayOfCal = startOfWeek(firstDayOfMonth);
      let lastDayOfMonth = endOfMonth(firstDayOfMonth);
      let lastDayOfCal = endOfWeek(lastDayOfMonth);
      let temp = new Date(firstDayOfCal);
      let days = [temp];
      while (temp < lastDayOfCal && days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      while (days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      this.calendarDays = days;
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
      this.getDays();
    },
    previousMonth() {
      this.currentMonth = addMonths(this.currentMonth, -1);
      this.getDays();
    },
    changeDay(d) {
      this.currentDay = d;
      this.selectedSlot = '';
      this.selectedSlotEndTime = '';
      this.selectedSlotStartTime = '';
      this.showForm = false;
    },
    hasAvailableSlots(d) {
      let slotsAvailable = false;
      for (let slot of this.eventSlots) {
        if (this.isDateSame(slot.startTime, d, 'day')) {
          slotsAvailable = true;
        }
      }
      return slotsAvailable;
    },
  },
  computed: {
    currentDaySlots() {
      let slots = [];
      for (let slot of this.eventSlots) {
        if (this.isDateSame(slot.startTime, this.currentDay, 'day')) {
          slots.push(slot);
        }
      }
      return slots;
    },
  },
};
</script>
